import type { RhinoRead, RhinoRecordIdentifier } from '@rhino-project/core';

// The grid is fed from index queries and its spec builds rows by hand, so each
// shape states the fields the grid reads and takes their types from the
// payload.
export type ProjectionPosition = Pick<RhinoRead<'position'>, 'id'> &
  Partial<Pick<RhinoRead<'position'>, 'name' | 'ancestry' | 'abbreviation'>>;

export type ProjectionRosterStatus = Partial<
  Pick<RhinoRead<'roster_status'>, 'id' | 'name' | 'status_type'>
>;

export type ProjectionTeamSeason = Pick<RhinoRead<'team_season'>, 'id'> &
  Partial<Pick<RhinoRead<'team_season'>, 'display_name' | 'status'>> & {
    season?: Partial<Pick<RhinoRead<'season'>, 'name' | 'starts_on'>> | null;
  };

// tags and player_tags stay unknown until the payload carries them.
export type ProjectionPlayer = Pick<RhinoRead<'player'>, 'id'> &
  Partial<Pick<RhinoRead<'player'>, 'display_name'>> & {
    positions?: ProjectionPosition[] | null;
    player_positions?: { position?: ProjectionPosition | null }[] | null;
    tags?: unknown;
    player_tags?: unknown;
  };

export type ProjectionRoster = {
  id: RhinoRecordIdentifier;
  player?: ProjectionPlayer | null;
  roster_status?: ProjectionRosterStatus | null;
  team_season?: ProjectionTeamSeason | RhinoRecordIdentifier | null;
};

// The leading column is the roster as it stands; the rest are the classes
// being recruited into. They count different things — see `buildProjectionGrid`
// — so every consumer has to be able to tell them apart.
export type ProjectionColumnKind = 'current' | 'class';

export interface ProjectionColumn {
  key: string;
  teamSeason: ProjectionTeamSeason;
  label: string;
  startsOn: string | null;
  kind: ProjectionColumnKind;
}

export interface ProjectionRow {
  key: string;
  label: string;
}

export interface ProjectionEntry {
  roster: ProjectionRoster;
  player: ProjectionPlayer;
  // True when this committed player already holds a spot on the current
  // roster, i.e. they carry forward rather than arriving. Always false in the
  // current column, where there is nothing to return from.
  isReturning: boolean;
}

export interface ProjectionCell {
  entries: ProjectionEntry[];
  count: number;
  returning: number;
}

export interface ProjectionGrid {
  columns: ProjectionColumn[];
  rows: ProjectionRow[];
  cells: Record<string, ProjectionCell>;
  columnTotals: Record<string, number>;
  hasCommitments: boolean;
}

export const UNASSIGNED_ROW_KEY = 'unassigned';

export const cellKey = (rowKey: string, columnKey: string) =>
  `${rowKey}|${columnKey}`;

// A prospect only counts against a roster hole once they have accepted the
// spot — the terminal planning status, the same end state
// Roster#notify_roster_accepted and TeamSeason#roster_accepted key on.
// Everyone in an earlier lane is still the board's business.
export const isCommitted = (
  roster: Pick<ProjectionRoster, 'roster_status'> | undefined,
  terminalStatus: ProjectionRosterStatus | null | undefined
): boolean => {
  if (!terminalStatus) return false;
  const status = roster?.roster_status;
  if (!status) return false;
  if (status.id != null && terminalStatus.id != null) {
    return String(status.id) === String(terminalStatus.id);
  }
  return status.name != null && status.name === terminalStatus.name;
};

const tagName = (tag: unknown): string | null => {
  if (typeof tag === 'string') return tag;
  if (tag && typeof tag === 'object') {
    const record = tag as Record<string, unknown>;
    const nested = record.tag;
    if (nested && typeof nested === 'object') {
      return tagName(nested);
    }
    if (typeof record.name === 'string') return record.name;
    if (typeof record.display_name === 'string') return record.display_name;
  }
  return null;
};

// Tags ship in #477 — accept either a `tags` array or `player_tags` join
// records, whichever the payload ends up carrying.
export const playerTagNames = (
  player: Pick<ProjectionPlayer, 'tags' | 'player_tags'> | undefined
): string[] => {
  const source = Array.isArray(player?.tags)
    ? (player?.tags as unknown[])
    : Array.isArray(player?.player_tags)
      ? (player?.player_tags as unknown[])
      : [];
  return source
    .map(tagName)
    .filter((name): name is string => name !== null && name.length > 0);
};

// A player listing two positions is counted once, in the group of the first
// (primary) one — otherwise a Forward/Defense tweener would fill two holes.
const primaryPosition = (
  player: ProjectionPlayer | undefined
): ProjectionPosition | null => {
  const positions = player?.positions;
  if (positions?.length) return positions[0];
  const joined = player?.player_positions?.[0]?.position;
  return joined ?? null;
};

// Positions use the ancestry gem's materialized path: a root has a null
// ancestry, a descendant's ancestry is "/"-separated ancestor ids starting
// at the root. Prefer the canonical record from the positions index, but a
// bare embedded record with its own ancestry still resolves.
export const rootPositionKey = (
  position: ProjectionPosition | null | undefined,
  positionsById: Map<string, ProjectionPosition>
): string | null => {
  if (!position) return null;
  const canonical = positionsById.get(String(position.id)) ?? position;
  const ancestry = canonical.ancestry;
  if (typeof ancestry === 'string' && ancestry.length > 0) {
    return ancestry.split('/')[0];
  }
  return canonical.id != null ? String(canonical.id) : null;
};

const isRoot = (record: ProjectionPosition) =>
  !(typeof record.ancestry === 'string' && record.ancestry.length > 0);

const seasonStartsOn = (teamSeason: ProjectionTeamSeason): string | null => {
  const season = teamSeason.season;
  const startsOn = season?.starts_on;
  return typeof startsOn === 'string' && startsOn.length > 0 ? startsOn : null;
};

const seasonLabel = (teamSeason: ProjectionTeamSeason): string => {
  const season = teamSeason.season;
  if (typeof season?.name === 'string' && season.name.length > 0) {
    return season.name;
  }
  if (typeof teamSeason.display_name === 'string') {
    return teamSeason.display_name;
  }
  return String(teamSeason.id);
};

// An archived roster row is a player who left, not one the program has.
export const isNotArchived = (
  roster: Pick<ProjectionRoster, 'roster_status'> | undefined
): boolean => roster?.roster_status?.status_type !== 'archived';

const emptyCell = (): ProjectionCell => ({
  entries: [],
  count: 0,
  returning: 0
});

interface BuildProjectionGridArgs {
  activeTeamSeason?: ProjectionTeamSeason | null;
  activeRosters?: ProjectionRoster[];
  teamSeasons: ProjectionTeamSeason[];
  rosters: ProjectionRoster[];
  positions: ProjectionPosition[];
  terminalStatus: ProjectionRosterStatus | null | undefined;
}

/**
 * The grid reads left to right as a timeline: the roster as it stands today,
 * then each class being recruited into.
 *
 * The two kinds of column deliberately count different things. The current
 * column is every non-archived active roster row — those players simply are on
 * the roster. A class column is only the players who have accepted a spot for
 * that season. Callers must label the difference rather than leave a reader to
 * infer it from a bare number.
 */
export const buildProjectionGrid = ({
  activeTeamSeason,
  activeRosters = [],
  teamSeasons,
  rosters,
  positions,
  terminalStatus
}: BuildProjectionGridArgs): ProjectionGrid => {
  // Planning seasons ARE the recruiting classes — a program's 2028-29 plan is
  // its 2028-29 class, whatever mix of graduation years fills it. Order them
  // by when the season starts so the columns read forward in time.
  const classColumns: ProjectionColumn[] = [...teamSeasons]
    .sort((a, b) => {
      const aStart = seasonStartsOn(a);
      const bStart = seasonStartsOn(b);
      if (aStart !== bStart) {
        if (aStart === null) return 1;
        if (bStart === null) return -1;
        return aStart < bStart ? -1 : 1;
      }
      return String(a.id).localeCompare(String(b.id), undefined, {
        numeric: true
      });
    })
    .map((teamSeason) => ({
      key: String(teamSeason.id),
      teamSeason,
      label: seasonLabel(teamSeason),
      startsOn: seasonStartsOn(teamSeason),
      kind: 'class' as const
    }));

  const currentRosters = activeTeamSeason
    ? activeRosters.filter(isNotArchived)
    : [];
  const currentColumn: ProjectionColumn | null = activeTeamSeason
    ? {
        key: String(activeTeamSeason.id),
        teamSeason: activeTeamSeason,
        label: seasonLabel(activeTeamSeason),
        startsOn: seasonStartsOn(activeTeamSeason),
        kind: 'current'
      }
    : null;

  const columns = currentColumn
    ? [currentColumn, ...classColumns]
    : classColumns;

  // A committed player already on the current roster is returning, not an
  // arrival — the same person occupying a later class, which is why nothing
  // totals across columns.
  const currentPlayerIds = new Set(
    currentRosters.map((roster) => String(roster.player?.id))
  );

  const positionsById = new Map(positions.map((p) => [String(p.id), p]));

  const rootRows: ProjectionRow[] = positions.filter(isRoot).map((p) => ({
    key: String(p.id),
    label: p.name ?? String(p.id)
  }));
  const rootKeys = new Set(rootRows.map((row) => row.key));

  const cells: Record<string, ProjectionCell> = {};
  const classColumnKeys = new Set(classColumns.map((column) => column.key));
  let hasUnassigned = false;

  const place = (
    roster: ProjectionRoster,
    columnKey: string,
    isReturning: boolean
  ) => {
    const player = roster.player ?? ({} as ProjectionPlayer);
    const resolvedRoot = rootPositionKey(
      primaryPosition(player),
      positionsById
    );
    let rowKey: string;
    if (resolvedRoot !== null && rootKeys.has(resolvedRoot)) {
      rowKey = resolvedRoot;
    } else {
      hasUnassigned = true;
      rowKey = UNASSIGNED_ROW_KEY;
    }

    const cell = (cells[cellKey(rowKey, columnKey)] ??= emptyCell());
    cell.entries.push({ roster, player, isReturning });
    cell.count += 1;
    if (isReturning) cell.returning += 1;
  };

  if (currentColumn) {
    for (const roster of currentRosters) {
      place(roster, currentColumn.key, false);
    }
  }

  for (const roster of rosters) {
    if (!isCommitted(roster, terminalStatus)) continue;

    const teamSeason = roster.team_season;
    const columnKey = String(
      (teamSeason && typeof teamSeason === 'object'
        ? teamSeason.id
        : teamSeason) ?? ''
    );
    if (!classColumnKeys.has(columnKey)) continue;

    const playerId = String(roster.player?.id);
    place(roster, columnKey, currentPlayerIds.has(playerId));
  }

  const rows = hasUnassigned
    ? [
        ...rootRows,
        {
          key: UNASSIGNED_ROW_KEY,
          label: 'Unassigned'
        }
      ]
    : rootRows;

  // Only per-column totals are meaningful. A player is not spent when their
  // season arrives — they carry forward into the seasons after it — so summing
  // a position group across columns would count the same player once per
  // remaining season.
  const columnTotals: Record<string, number> = {};
  let hasCommitments = false;

  for (const column of columns) {
    let columnTotal = 0;
    for (const row of rows) {
      const key = cellKey(row.key, column.key);
      const cell = (cells[key] ??= emptyCell());
      columnTotal += cell.count;
    }
    columnTotals[column.key] = columnTotal;
    if (column.kind === 'class' && columnTotal > 0) hasCommitments = true;
  }

  return { columns, rows, cells, columnTotals, hasCommitments };
};
